import React, { useEffect, useState } from 'react';
import { Col, Form, Row, notification, Button } from 'antd';
import * as S from './CustomerInfoForm.styles';
import { BaseInput } from '@app/components/common/inputs/BaseInput/BaseInput';
import { BaseSelect } from '@app/components/common/selects/BaseSelect/BaseSelect';
import { Customer } from '@app/models/customer/Customer';
import { getVerifiedCustomers, viewCustomer } from '@app/api/customer.api';
import { getDepartmentById } from '@app/api/departments.api';
import { SearchOutlined } from '@ant-design/icons';
import { DropdownOption } from '@app/models/DropdownOption';

interface CustomerInfoFormProps {
  form: any;
  type: 'edit' | 'create' | 'view';
  customerInfo?: any;
  createType?: string | null;
  customerOptions?: any[];
  departmentOptions?: any[];
  importTypee?: string;
  onPrOptionsChange?: (options: DropdownOption[]) => void;
  onPcOptionsChange?: (options: DropdownOption[]) => void;
  onEmailSearch?: (customerId: number | null) => void;
}

export const CustomerInfoForm: React.FC<CustomerInfoFormProps> = ({
  form,
  type,
  customerInfo,
  createType,
  customerOptions,
  departmentOptions,
  importTypee,
  onPrOptionsChange,
  onPcOptionsChange,
  onEmailSearch,
}) => {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [customerEmail, setCustomerEmail] = useState<string>('');
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string | null>(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userType = user?.usertype;
  const [prOptions, setPrOptions] = useState<DropdownOption[]>([]);
  const [pcOptions, setPcOptions] = useState<DropdownOption[]>([]);
  const [customerID, setCustomerID] = useState<number | null>(null);

  // console.log('received customer info:', customerInfo);

  useEffect(() => {
    if (importTypee && importTypee !== 'NORMAL' && importTypee !== 'NO_OBJECTION' && importTypee !== 'NON_RELEVANT') {
      return;
    }
    if (customerInfo && createType !== 'LAB' && createType !== 'NOMPAY') {
      form.setFieldsValue({
        id: customerInfo.id,
        email: customerInfo?.customer?.email,
        contact_no: customerInfo?.customer?.contact_no ?? '071234567',
        name: customerInfo.name,
        status: customerInfo?.customer?.status ?? 'Active',
        nic: customerInfo?.customer?.nic ?? '123456789V',
        customer_type: customerInfo?.customer?.customer_type ?? 'REGULAR',
        address: customerInfo?.customer?.address ?? '123 Main St, Colombo',
        business_name: customerInfo?.additional_info?.business_name,
        business_address: customerInfo?.customer?.additional_info?.business_address,
        company_type: customerInfo?.additional_info?.company_type,
        business_registration_no: customerInfo?.additional_info?.business_reg_no,
      });
    } else if (!customerInfo && createType !== 'LAB' && createType !== 'NOMPAY') {
      // Use local storage user data when customerInfo is null
      form.setFieldsValue({
        id: user?.customer?.id ?? user?.id,
        email: user?.email ?? '',
        contact_no: user?.customer?.contact_no ?? '',
        name: user?.customer?.name || user?.name || '',
        status: user?.status ?? 'Active',
        nic: user?.customer?.nic ?? '',
        customer_type: user?.customer?.customer_type ?? 'One Time',
        address: user?.customer?.address ?? '',
        business_name: user?.customer?.additional_info?.business_name ?? '',
        business_address: user?.customer?.additional_info?.business_address ?? '',
        company_type: user?.customer?.additional_info?.company_type ?? '',
        business_registration_no: user?.customer?.additional_info?.business_reg_no ?? '',
      });
    }
  }, [customerInfo, createType, form, user, importTypee]);

  useEffect(() => {
    const fetchDepartmentData = async () => {
      if ((createType === 'LAB' || createType === 'NOMPAY') && selectedDepartmentId) {
        try {
          const data = await getDepartmentById(Number(selectedDepartmentId));
          form.resetFields();
          form.setFieldsValue({
            department_id: data.department.id,
            department_code: data.department.code,
            department_name: data.department.name,
            department_address: data.department.address,
            department_description: data.department.description,
            department_contact_no: data.department.contact_no,
          });

          // Reset customer email input when a department is selected
          setCustomerEmail('');
          setSelectedCustomerId(null);
        } catch (error) {
          console.error('Failed to fetch department data:', error);
          notification.error({
            message: 'Error',
            description: 'Failed to fetch department data. Please try again.',
          });
        }
      }
    };

    fetchDepartmentData();
  }, [selectedDepartmentId, createType, form]);

  const handleSearchCustomer = async () => {
    if (!customerEmail || !validateEmail(customerEmail)) {
      notification.error({
        message: 'Error',
        description: 'Please enter a valid email address.',
      });
      return;
    }

    // Reset department selection if any
    setSelectedDepartmentId(null);
    form.resetFields();

    setSearchLoading(true);
    try {
      const data = await getVerifiedCustomers(customerEmail);
      if (data) {
        setSelectedCustomerId(data.id.toString());
        form.setFieldsValue({
          id: data.id,
          email: data.user?.email,
          contact_no: data.contact_no,
          name: data.name,
          status: data.user?.status,
          nic: data.nic,
          customer_type: data.customer_type,
          address: data.address,
          business_name: data.additional_info?.business_name,
          business_address: data.additional_info?.business_address,
          company_type: data.additional_info?.company_type,
          business_registration_no: data.additional_info?.business_reg_no,
        });

        setCustomerID(data.id);
        onEmailSearch?.(data.id);

        if (type === 'create' && importTypee !== 'NORMAL') {
          const pr = data.pr_options || [];
          const pc = data.pc_options || [];

          setPrOptions(pr);
          setPcOptions(pc);

          // Notify parent immediately
          onPrOptionsChange?.(pr);
          onPcOptionsChange?.(pc);
        }
      } else {
        notification.warning({
          message: 'No Customer Found',
          description: 'No customer was found with that email address.',
        });
      }
    } catch (error: any) {
      console.error('Failed to fetch customer data:', error);
      notification.error({
        message: 'Error',
        description: error.message || 'Failed to fetch customer data. Please try again.',
      });
    } finally {
      setSearchLoading(false);
    }
  };

  const validateEmail = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleDepartmentSelect = (value: string) => {
    setSelectedDepartmentId(value);
    // Reset customer info
    setSelectedCustomerId(null);
    setCustomerEmail('');
    form.resetFields();
  };

  useEffect(() => {
    //console.log('Passed importTypee:', importTypee);
    if (importTypee === 'NORMAL') {
      setCustomerEmail('');
      setSelectedCustomerId(null);
      setSelectedDepartmentId(null);
    }
  }, [importTypee]);

  const shouldShowCustomerInfo = !selectedCustomerId && !selectedDepartmentId;

  const showDepartmentSection =
    (type === 'view' || type === 'edit') && customerInfo?.referenceable_label === 'DEPARTMENT';

  const showCustomerSection =
    type === 'create' || ((type === 'view' || type === 'edit') && customerInfo?.referenceable_label !== 'DEPARTMENT');

  return (
    <S.CustomerInfoFormWrapper>
      {(type === 'create' || type === 'edit') &&
        createType !== 'Follow-Up' &&
        createType !== 'PC' &&
        (createType === 'LAB' ||
          createType === 'NOMPAY' ||
          importTypee === 'NON_COMPLIANCE_1' ||
          importTypee === 'NON_COMPLIANCE_3') &&
        userType === 'ADMIN' && (
          <>
            <Row gutter={16} justify="start" align="middle">
              <Col xs={24} md={24} lg={8}>
                <Form.Item>
                  <BaseInput.Search
                    placeholder="Enter customer email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    onSearch={handleSearchCustomer}
                    enterButton={
                      <Button type="primary" loading={searchLoading}>
                        <SearchOutlined />
                      </Button>
                    }
                  />
                </Form.Item>
              </Col>

              {createType !== 'NOMPAY' && createType !== null && !importTypee && (
                <Col xs={24} md={24} lg={8}>
                  <Form.Item>
                    <BaseSelect
                      showSearch
                      placeholder="Select Department"
                      value={selectedDepartmentId}
                      onChange={(value) => handleDepartmentSelect(value as string)}
                      options={departmentOptions}
                      optionFilterProp="label"
                      filterOption={(input, option) =>
                        (option?.label as string).toLowerCase().includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>

            <br />
          </>
        )}
      <Form
        form={form}
        name="customerInfoForm"
        size="small"
        labelCol={{ lg: { span: 8 }, md: { span: 8 }, sm: { span: 24 } }}
        labelAlign="left"
        labelWrap
        wrapperCol={{ lg: { span: 20 }, md: { span: 20 }, sm: { span: 24 } }}
      >
        {/* Customer Info Section */}
        {showCustomerSection && (selectedCustomerId || shouldShowCustomerInfo) && (
          <>
            <h3>General Info</h3>
            <Row gutter={16}>
              <Col xs={24} md={24} lg={8}>
                <Form.Item name="id" label="UID">
                  <BaseInput readOnly />
                </Form.Item>
                <Form.Item name="email" label="Email">
                  <BaseInput readOnly />
                </Form.Item>
                <Form.Item name="contact_no" label="Contact No">
                  <BaseInput readOnly />
                </Form.Item>
              </Col>
              <Col xs={24} md={24} lg={8}>
                <Form.Item name="name" label="Customer Name">
                  <BaseInput readOnly />
                </Form.Item>
                <Form.Item name="status" label="Customer Status">
                  <BaseInput readOnly />
                </Form.Item>
                <Form.Item name="nic" label="NIC No">
                  <BaseInput readOnly />
                </Form.Item>
              </Col>
              <Col xs={24} md={24} lg={8}>
                <Form.Item name="customer_type" label="Customer Type">
                  <BaseInput readOnly />
                </Form.Item>
                <Form.Item name="address" label="Address">
                  <BaseInput readOnly />
                </Form.Item>
              </Col>
            </Row>
            <br />
            <h3>Business Info</h3>
            <Row gutter={16}>
              <Col xs={24} md={24} lg={8}>
                <Form.Item name="business_name" label="Business Name">
                  <BaseInput readOnly />
                </Form.Item>
              </Col>
              <Col xs={24} md={24} lg={8}>
                <Form.Item name="business_address" label="Business Address">
                  <BaseInput readOnly />
                </Form.Item>
              </Col>
              <Col xs={24} md={24} lg={8}>
                <Form.Item name="company_type" label="Company Type">
                  <BaseInput readOnly />
                </Form.Item>
              </Col>
              <Col xs={24} md={24} lg={8}>
                <Form.Item name="business_registration_no" label="Business Registration No">
                  <BaseInput readOnly />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        {/* Department Info Section */}
        {(((type === 'create' || type === 'edit') &&
          createType === 'LAB' &&
          userType === 'ADMIN' &&
          selectedDepartmentId) ||
          showDepartmentSection) && (
          <>
            <h3>Department Info</h3>
            <Row gutter={16}>
              <Form.Item name="department_id" noStyle>
                <BaseInput type="hidden" />
              </Form.Item>

              <Col xs={24} md={24} lg={8}>
                <Form.Item name="department_code" label="Department Code">
                  <BaseInput readOnly />
                </Form.Item>
              </Col>
              <Col xs={24} md={24} lg={8}>
                <Form.Item name="department_name" label="Department Name">
                  <BaseInput readOnly />
                </Form.Item>
              </Col>
              <Col xs={24} md={24} lg={8}>
                <Form.Item name="department_address" label="Address">
                  <BaseInput readOnly />
                </Form.Item>
              </Col>
              <Col xs={24} md={24} lg={8}>
                <Form.Item name="department_description" label="Description">
                  <BaseInput readOnly />
                </Form.Item>
              </Col>
              <Col xs={24} md={24} lg={8}>
                <Form.Item name="department_contact_no" label="Contact No">
                  <BaseInput readOnly />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}
      </Form>
    </S.CustomerInfoFormWrapper>
  );
};
